import { useMemo } from 'preact/hooks'
import { useFilters } from 'libs/searchspring/hooks/useFilters'
import { ColorSwatch, getFilterDisplayCustomisations, IconImage } from 'libs/searchspring/components/collection/filters/FilterCustomisations'
import clsx from 'clsx'
import { RANGE_INDICATOR } from './FilterUtils'

export function CustomFilter({ filter }) {
  if (!filter) return null
  const { handleToggleFilter, activeFilters, fetchedFilters } = useFilters()

  const fetchedFilter = useMemo(() => {
    return fetchedFilters.find((current) => current.field === filter)
  }, [fetchedFilters])

  if (!fetchedFilter) return null

  const filterCustomizations = useMemo(() => {
    return getFilterDisplayCustomisations(filter)
  }, [filter])

  const values = useMemo(() => {
    return (
      fetchedFilter.values.map((item) => {
        const value = item.type === 'range' ? `${item.low}${RANGE_INDICATOR}${item.high}` : item.value
        const valueWithKey = `${fetchedFilter.field}:${value}`

        const fetchedValue = fetchedFilter?.values?.find((currentValue) => {
          return currentValue.label === item.label
        })
        const customValue = filterCustomizations.values?.find((customItem) => customItem.value === item.label) || {}

        const {
          icon = false,
          icon_id: iconID = false,
          icon_aspect_ratio: aspectRatio = false,
          icon_width: iconWidth = false,
          filter_colour: filterColour = false
        } = customValue

        const iconObj = icon
          ? { icon, aspectRatio: aspectRatio || 1, iconWidth }
          : iconID
            ? { iconID, aspectRatio: aspectRatio || 1, iconWidth }
            : false

        return {
          label: item.label,
          value: valueWithKey,
          count: fetchedValue?.count,
          checked: activeFilters?.has(valueWithKey),
          disabled: !fetchedValue,
          color: filterColour,
          icon: iconObj
        }
      }) ?? []
    )
  }, [activeFilters, fetchedFilter])

  if (!values.length === 0) return null

  return (
    <div className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between md:p-6">
      <p className="paragraph-m">Shop by {fetchedFilter.label}</p>
      <div className="flex max-w-full grow gap-2 overflow-x-auto md:justify-end">
        {values.map((item) => (
          <div className="group/filter-item relative flex w-fit cursor-pointer items-start gap-x-0.5" key={item.value}>
            <input
              id={item.value}
              type="checkbox"
              name={filter.field}
              checked={item.checked}
              disabled={item.disabled}
              onClick={() => handleToggleFilter(item.value)}
              aria-label={item.label}
              className={clsx(
                'peer field-checkbox shrink-0 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 group-hover/filter-item:border-primary group-hover/filter-item:bg-primary',
                item.color || item.icon
                  ? 'overflow-hidden absolute z-2 !size-1.5 rounded-full opacity-0 inset-tl'
                  : 'mt-0.25'
              )}
            />
            <label
              for={item.value}
              className="flex grow cursor-pointer items-start peer-disabled:pointer-events-none peer-disabled:cursor-not-allowed peer-disabled:opacity-50 peer-checked:[&_.swatch-inner]:outline-brand-black"
            >
              {item.color && <ColorSwatch color={item.color} />}
              {item.icon && <IconImage iconObject={item.icon} />}
              <span className="paragraph-m">
                {item.label}
                <span className="paragraph-xs text-type-black-secondary">&nbsp;({item.count ?? 0})</span>
              </span>
            </label>
          </div>
        ))}
      </div>
    </div>
  )
}
